import { CompanyReportCategoryStatusStatusEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Scope3CategoryBadge from '@shared/components/content/scope-3-category-badge';
import Text from '@shared/components/content/text';
import { Card, CardContent } from '@shared/components/ui/card';
import { cn, formatNumber } from '@shared/lib/utils';
import { Link } from 'react-router-dom';
import { Scope3Category } from '../scopes';

interface HubCardProps {
  icon: React.ReactNode;
  name: string;
  scope3Category?: Scope3Category;
  totalEmissions: number | null;
  description: string;
  href: string;
  status?: CompanyReportCategoryStatusStatusEnum;
  included?: boolean;
  slug: string;
  disabled?: boolean;
  badges?: string[];
}

const cardDescription: { [key: string]: string } = {
  food: 'Food and drink you have purchased',
};

function getStatus(status?: CompanyReportCategoryStatusStatusEnum) {
  switch (status) {
    case CompanyReportCategoryStatusStatusEnum.complete:
      return (
        <>
          <Icons.check className="mr-1 text-secondary-400" size={14} />
          <Text variant="subtle">Complete</Text>
        </>
      );
    case CompanyReportCategoryStatusStatusEnum.no_emissions:
      return (
        <>
          <Icons.x className="mr-1 text-gray-400" size={14} />
          <Text variant="subtle">No emissions</Text>
        </>
      );
    case CompanyReportCategoryStatusStatusEnum.not_required:
      return (
        <>
          <Icons.x className="mr-1 text-gray-400" size={14} />
          <Text variant="subtle">Not required</Text>
        </>
      );
    case CompanyReportCategoryStatusStatusEnum.data_unavailable:
      return (
        <>
          <Icons.x className="mr-1 text-gray-400" size={14} />
          <Text variant="subtle">Data unavailable</Text>
        </>
      );
    default:
      return null;
  }
}

function EmissionsDisplay({
  status,
  totalEmissions,
}: {
  status?: CompanyReportCategoryStatusStatusEnum;
  totalEmissions: number | null;
}) {
  const isExcluded =
    status === CompanyReportCategoryStatusStatusEnum.data_unavailable ||
    status === CompanyReportCategoryStatusStatusEnum.not_required;

  const showZero =
    status === CompanyReportCategoryStatusStatusEnum.no_emissions ||
    !totalEmissions;

  return (
    <span
      className={cn('text-xl', {
        'text-gray-500 pr-1': isExcluded,
        'text-teal-400': !isExcluded && showZero,
        'text-teal-600': !isExcluded && !showZero,
      })}
    >
      {isExcluded ? '-' : showZero ? '0' : formatNumber(totalEmissions!)}
    </span>
  );
}

export default function HubCard({
  icon,
  name,
  scope3Category,
  totalEmissions,
  description,
  href,
  status,
  slug,
  disabled = false,
}: HubCardProps) {
  return (
    <Link
      to={href}
      className={cn('', {
        'cursor-not-allowed': disabled,
        'cursor-pointer': disabled,
      })}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
      }}
    >
      <Card
        className={cn('shadow-md mb-2', {
          'opacity-50': disabled,
        })}
      >
        <CardContent className="flex items-center px-4 pb-2 pt-3">
          <div>{icon}</div>
          <span className="ml-2 text-md">{name}</span>
          <div className="ml-auto flex items-baseline space-x-1">
            <EmissionsDisplay status={status} totalEmissions={totalEmissions} />
            <Text variant="unit">
              tCO<sub>2</sub>e
            </Text>
          </div>
        </CardContent>
        {scope3Category && (
          <CardContent className="px-4 pb-4">
            <Scope3CategoryBadge category={scope3Category} size="sm" />
          </CardContent>
        )}
        <CardContent className="flex justify-between px-4 pb-4">
          <Text variant="subtle">{cardDescription[slug] || description}</Text>
          <span className="flex items-center">
            {!disabled && getStatus(status)}
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}
